import React from "react";
import { AreaTrend } from "@/features/investors/components/charts/AreaTrend";
import { Donut } from "@/features/investors/components/charts/Donut";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    InsightHtml,
    SectionHead,
    Unavailable,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import {
    InvestorsDashboardData,
    MetricValue,
} from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import {
    DeltaFormat,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const COMPOSITION_COLORS = [
    CHART_COLORS.blue,
    CHART_COLORS.teal,
    CHART_COLORS.violet,
    CHART_COLORS.lime,
    CHART_COLORS.orange,
    CHART_COLORS.pink,
];

type ScoreCell = {
    label: string;
    metric: MetricValue;
    render: (value: number) => string;
    format: DeltaFormat;
    invert?: boolean;
};

const scoreDelta = (cell: ScoreCell): { text: string; good: boolean } | null => {
    const { metric, format } = cell;
    if (metric.value === null || metric.previous === null) return null;
    const difference = metric.value - metric.previous;
    const text =
        format === "pct"
            ? metric.deltaPct === null
                ? null
                : fmt.deltaPct(metric.deltaPct)
            : format === "pp"
              ? fmt.deltaPp(difference)
              : fmt.deltaBps(difference * 100);
    if (text === null) return null;
    const up = format === "pct" ? (metric.deltaPct ?? 0) >= 0 : difference >= 0;
    return { text, good: cell.invert === true ? !up : up };
};

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const MonetizationSection: React.FC<Props> = ({ data, insights }) => {
    const { monetization, executive } = data;
    const monthly = data.growth.monthly;

    const cells: ScoreCell[] = [
        { label: "Revenue / operation", metric: monetization.revenuePerOperation, render: fmt.moneyPrecise, format: "pct" },
        { label: "Profit / operation", metric: monetization.profitPerOperation, render: fmt.moneyPrecise, format: "pct" },
        { label: "Revenue / active account", metric: monetization.revenuePerAccount, render: fmt.moneyPrecise, format: "pct" },
        { label: "Profit / active account", metric: monetization.profitPerAccount, render: fmt.moneyPrecise, format: "pct" },
        { label: "Take rate", metric: executive.takeRatePct, render: (value) => value.toFixed(2) + "%", format: "bps" },
        { label: "Gross margin", metric: executive.grossMarginPct, render: (value) => fmt.pct(value), format: "pp" },
        { label: "Fee realization", metric: monetization.feeRealizationPct, render: (value) => fmt.pct(value), format: "pp" },
        { label: "Negative-margin ops", metric: monetization.negativeMarginRatePct, render: (value) => fmt.pct(value, 2), format: "pp", invert: true },
    ];

    const marginByMonth = monthly.map((month) =>
        month.revenue === 0 ? 0 : (month.profit / month.revenue) * 100
    );

    return (
        <section className="sec" id="sec-money">
            <SectionHead
                index="03"
                title="Monetization & unit economics"
                question={
                    <>
                        Question: <em>Are we making money efficiently?</em>
                    </>
                }
            />
            <div className="score">
                {cells.map((cell) => {
                    const delta = scoreDelta(cell);
                    return (
                        <div key={cell.label} className="cell">
                            <div className="l">{cell.label}</div>
                            <div className="v">
                                {cell.metric.status === "unavailable" || cell.metric.value === null ? (
                                    <Unavailable reason={cell.metric.reason} />
                                ) : (
                                    cell.render(cell.metric.value)
                                )}
                            </div>
                            {delta !== null ? (
                                <div className={`d ${delta.good ? "up" : "down"}`}>
                                    {delta.text}{" "}
                                    <span style={{ color: "var(--sub)", fontWeight: 500 }}>QoQ</span>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 20 }}>
                <div className="card">
                    <CardHeader
                        title="Revenue composition"
                        sub={`${data.period.label} · by fee type${executive.revenue.value !== null ? ` · ${fmt.moneyCompact(executive.revenue.value)} total` : ""}`}
                    />
                    <Donut
                        segments={monetization.revenueComposition.map((item, index) => ({
                            label: item.label,
                            value: item.value,
                            color: COMPOSITION_COLORS[index % COMPOSITION_COLORS.length],
                        }))}
                        centerTop={
                            executive.revenue.value !== null
                                ? fmt.moneyCompact(executive.revenue.value)
                                : "—"
                        }
                        centerSub="REVENUE"
                        formatValue={fmt.moneyCompact}
                    />
                </div>
                <div className="card">
                    <CardHeader title="Margin evolution" sub="Gross margin by month" />
                    <AreaTrend
                        values={marginByMonth}
                        labels={monthly.map((month) => month.label)}
                        color={CHART_COLORS.lime}
                        min={Math.min(0, ...marginByMonth)}
                        max={Math.max(...marginByMonth) * 1.25 || 1}
                        formatValue={(value) => fmt.pct(value)}
                    />
                </div>
            </div>
            <div className="card" style={{ marginTop: 20 }}>
                <CardHeader
                    title="Performance by operation type"
                    sub="Completed operations · GMV in reporting currency (EUR)"
                />
                <table className="ptable">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th style={{ textAlign: "right" }}>Ops</th>
                            <th style={{ textAlign: "right" }}>GMV</th>
                            <th style={{ textAlign: "right" }}>Revenue</th>
                            <th style={{ textAlign: "right" }}>Profit</th>
                            <th style={{ textAlign: "right" }}>Margin</th>
                            <th style={{ textAlign: "right" }}>Take rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {monetization.operationTypes.map((row) => (
                            <tr key={row.operationType}>
                                <td>
                                    <span className="pname">
                                        <span
                                            className="pdot"
                                            style={{ background: CHART_COLORS.blue }}
                                        />
                                        {row.operationType}
                                    </span>
                                </td>
                                <td style={{ textAlign: "right" }}>{fmt.num(row.operations)}</td>
                                <td style={{ textAlign: "right" }}>{fmt.moneyCompact(row.gmv)}</td>
                                <td style={{ textAlign: "right" }}>{fmt.moneyCompact(row.revenue)}</td>
                                <td style={{ textAlign: "right" }}>{fmt.moneyCompact(row.profit)}</td>
                                <td style={{ textAlign: "right" }}>
                                    {row.marginPct !== null ? fmt.pct(row.marginPct) : "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    {row.takeRatePct !== null ? fmt.pct(row.takeRatePct, 2) : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.monetization} />
            </div>
        </section>
    );
};
